import { useMutation, useQueryClient } from '@tanstack/react-query';

import { addonRepair } from '@api/sdk.gen';

import { notifySuccess } from '@/portainer/services/notifications';
import { withError, withInvalidate } from '@/react-tools/react-query';

import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals/Modal/types';
import { buildConfirmButton } from '@@/modals/utils';

import { queryKeys } from '../query-keys';

const SUCCESS_MESSAGE =
  'The add-on regains access once Kubernetes propagates the new credential, which usually takes up to a minute.';

/**
 * useRepairAddon issues the add-on a fresh credential and republishes it: the
 * recovery path for one that is running but can no longer authenticate.
 *
 * Not a Helm operation — nothing is redeployed and the stored settings survive.
 */
export function useRepairAddon(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await addonRepair({ path: { id } });
      // Not in an onSuccess handler, which withInvalidate owns.
      notifySuccess('Credential repaired', SUCCESS_MESSAGE);
      return result;
    },
    ...withInvalidate(queryClient, [queryKeys.list(), queryKeys.detail(id)]),
    ...withError('Unable to repair the add-on credential'),
  });
}

/**
 * Confirms a repair. The previous credential stops working immediately, so an
 * add-on still using it is briefly locked out until the new one reaches its pod.
 */
export function confirmAddonRepair(displayName: string) {
  return confirm({
    title: 'Repair add-on credential',
    modalType: ModalType.Warn,
    message: `Issue ${displayName} a new credential? Its previous one stops working immediately, and it may be unable to read its settings for up to a minute. Its stored settings are not affected.`,
    confirmButton: buildConfirmButton('Repair'),
  });
}
